import { IconButton, styled } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

export const recordingPulseAnimation = {
  animate: { scale: [1, 1.1, 1] },
  transition: { repeat: Infinity, duration: 1 },
};

export const MOBILE_LANDSCAPE_MQ =
  "@media (orientation: landscape) and (max-height: 520px) and (pointer: coarse)";

export const GradientIconButton = styled(IconButton)(() => ({
  position: "absolute",
  right: "6px",
  top: "50%",
  transform: "translateY(-50%)",
  background: "linear-gradient(180deg, #28C9DC 0%, #7B49C1 100%)",
  color: "#FFFFFF",
  borderRadius: "12px",
  padding: "6px",
  "&:hover": {
    background: "linear-gradient(180deg, #20B6C8 0%, #7C4ABF 100%)",
  },
}));

export const getFooterOuterSx = (): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "center",
  pt: { xs: 1, sm: "10px" },
  px: { xs: 0, sm: "10px" },
  pb: 0,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  boxShadow: "none",
  [MOBILE_LANDSCAPE_MQ]: {
    pt: 0.25,
  },
});

export const getFooterShellSx = (
  footerShellBg: string,
  isDarkMode: boolean,
  options?: { overflowVisible?: boolean },
): SxProps<Theme> => ({
  position: "relative",
  alignSelf: { xs: "stretch", sm: "auto" },
  backgroundColor: footerShellBg,
  borderRadius: "18px 18px 0 0",
  px: { xs: 0, sm: 2 },
  overflow: options?.overflowVisible ? "visible" : "hidden",
  pt: 0,
  pb: 0,
  width: "100%",
  maxWidth: {
    xs: "100%",
    sm: "calc(100% - 72px)",
    md: 750,
  },
  ml: { xs: 0, sm: 0, md: "auto" },
  mr: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 0,
  marginBottom: 0,
  zIndex: 10,
  boxShadow: isDarkMode
    ? "0 8px 28px rgba(0,0,0,0.45)"
    : "0 6px 22px rgba(107, 70, 193, 0.1)",
  [MOBILE_LANDSCAPE_MQ]: {
    maxHeight:
      "min(72dvh, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)))",
    display: "flex",
    flexDirection: "column",
    overflow: options?.overflowVisible ? "visible" : "hidden",
  },
});

export const getFooterHintsScrollSx = (): SxProps<Theme> => ({
  flexShrink: 0,
  [MOBILE_LANDSCAPE_MQ]: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
  },
});

export const getFooterPinnedBottomSx = (): SxProps<Theme> => ({
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
});

export const getFooterInputRowSx = (
  footerInputRowBg: string,
  isSmallScreen: boolean,
): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: isSmallScreen ? 1 : 2,
  overflow: "visible",
  backgroundColor: footerInputRowBg,
  ...(isSmallScreen
    ? {
        mx: 2,
        mt: 0,
        mb: 0.35,
        pl: 1.25,
        pr: 0,
        py: 0.75,
      }
    : {
        mt: 0,
        mb: 0.35,
        px: 1.25,
        py: 0.75,
      }),
  [MOBILE_LANDSCAPE_MQ]: {
    mx: 1.25,
    py: 0.5,
    mb: 0.25,
  },
});

export const getMenuToggleRowSx = (isDarkMode: boolean): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  color: isDarkMode ? "#FFFFFF" : "#8B7BA8",
  "& svg path": { fill: "currentColor" },
});

export const getInputFormSx = (): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  gap: 2,
  minWidth: 0,
});

export const getInputFormColumnSx = (): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
});

export const getInputControlsRowSx = (): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: 2,
  minWidth: 0,
  overflow: "visible",
});

export const getAttachmentWrapperSx = (isMmkMode: boolean): SxProps<Theme> => ({
  position: "relative",
  opacity: isMmkMode ? 0.5 : 1,
});

export const getPaperclipButtonSx = (): SxProps<Theme> => ({
  color: "#6B7280",
  "&:hover": { backgroundColor: "#e0e0e0" },
});

export const getAttachmentPopupSx = (): SxProps<Theme> => ({
  position: "absolute",
  top: "100%",
  left: 0,
  mt: 1,
});

export const getInputAreaWrapperSx = (): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  position: "relative",
  flex: 1,
  overflow: "visible",
});

export const getInputSlideSx = (isMmkMode: boolean): SxProps<Theme> => ({
  width: "100%",
  position: "relative",
  opacity: isMmkMode ? 0.5 : 1,
  minHeight: 40,
  overflow: "visible",
});

export const getInputGradientBorderSx = (): SxProps<Theme> => ({
  borderRadius: "999px",
  p: "1.5px",
  background: "linear-gradient(135deg, #28C9DC 0%, #7B49C1 100%)",
});

export const getInputInnerSx = (): SxProps<Theme> => ({
  position: "relative",
  borderRadius: "999px",
  overflow: "hidden",
  bgcolor: "#FFFFFF",
});

export const getKeyboardIconButtonSx = (micColor: string): SxProps<Theme> => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  margin: "0 0.5rem",
  color: micColor,
  opacity: 0.4,
  cursor: "not-allowed",
  pointerEvents: "none",
});

export const getKeyboardIconSizeSx = (
  isSmallScreen: boolean,
): SxProps<Theme> => ({
  height: isSmallScreen ? "1.2rem" : "1.45rem",
  width: isSmallScreen ? "1.2rem" : "1.45rem",
});

export const getSendActionContainerSx = (
  options?: { locked?: boolean; recordingHold?: boolean },
): SxProps<Theme> => {
  if (options?.recordingHold) {
    return {
      position: "absolute",
      right: "-6px",
      top: "50%",
      transform: "translateY(-50%)",
      width: 64,
      height: 64,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "visible",
      zIndex: 5,
    };
  }

  if (options?.locked) {
    return {
      position: "absolute",
      right: "0px",
      top: "50%",
      transform: "translateY(-50%)",
      width: 44,
      height: 44,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "visible",
      zIndex: 5,
    };
  }

  return {
    position: "absolute",
    right: "4px",
    top: "4px",
    bottom: "4px",
    aspectRatio: "1 / 1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    zIndex: 5,
  };
};

export const getSendActionInnerSx = (isMmkMode: boolean): SxProps<Theme> => ({
  width: "100%",
  height: "100%",
  opacity: isMmkMode ? 0.5 : 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "visible",
});

export const getStopButtonSx = (
  micColor: string,
  micColorHover: string,
): SxProps<Theme> => ({
  color: micColor,
  borderRadius: "50%",
  width: "100%",
  height: "100%",
  p: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    display: "block",
  },
  "&:hover": {
    backgroundColor: micColorHover,
  },
});

export const getMicButtonSx = (isSmallScreen: boolean): SxProps<Theme> => ({
  background: "linear-gradient(180deg, #28C9DC 0%, #7B49C1 100%)",
  color: "#ffffff",
  borderRadius: "50%",
  width: "100%",
  height: "100%",
  p: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  touchAction: "none",
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
  "& svg:not(.MuiCircularProgress-svg)": {
    display: "block",
    width: isSmallScreen ? 16 : 18,
    height: isSmallScreen ? 16 : 18,
    pointerEvents: "none",
  },
  "&:hover": {
    background: "linear-gradient(180deg, #20B6C8 0%, #7C4ABF 100%)",
    transform: "scale(1.05)",
  },
});

export const getRecordingMicButtonSx = (
  isSmallScreen: boolean,
  options?: { glowing?: boolean; locked?: boolean },
): SxProps<Theme> => ({
  background: options?.locked ? "#7F1D1D" : "#3390EC",
  color: "#ffffff",
  borderRadius: "50%",
  width: "100%",
  height: "100%",
  p: 0,
  position: "relative",
  zIndex: 2,
  boxShadow: options?.glowing
    ? "0 0 0 4px rgba(51, 144, 236, 0.28), 0 10px 28px rgba(0, 0, 0, 0.22)"
    : "none",
  transition: "box-shadow 0.15s ease, background 0.15s ease",
  touchAction: "none",
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
  "& svg": {
    display: "block",
    width: isSmallScreen ? 26 : 30,
    height: isSmallScreen ? 26 : 30,
    pointerEvents: "none",
  },
  "&:hover": {
    background: options?.locked ? "#991B1B" : "#2B7FD4",
    boxShadow: options?.glowing
      ? "0 0 0 5px rgba(51, 144, 236, 0.34), 0 12px 30px rgba(0, 0, 0, 0.28)"
      : undefined,
  },
});

export const getTranscribingMicProgressSx = (): SxProps<Theme> => ({
  color: "#ffffff",
  display: "block",
  margin: 0,
  "& .MuiCircularProgress-svg": {
    display: "block",
  },
});

export const getSendSubmitButtonSx = (): SxProps<Theme> => ({
  position: "relative",
  right: "auto",
  top: "auto",
  transform: "none",
  borderRadius: "50%",
  width: "100%",
  height: "100%",
});

export const getFeedbackMobileSx = (): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  alignSelf: "center",
  ml: 1.25,
});

export const getTabStripSx =
  (tabStripBackground: string, isSmallScreen: boolean): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    alignItems: "stretch",
    justifyContent: "flex-start",
    alignSelf: "stretch",
    boxSizing: "border-box",
    width: isSmallScreen ? "100%" : `calc(100% + ${theme.spacing(4)})`,
    ml: isSmallScreen ? 0 : -2,
    mr: isSmallScreen ? 0 : -2,
    mt: 0,
    mb: 0,
    px: 0,
    pt: 0.35,
    paddingBottom: `calc(${theme.spacing(1.5)} + env(safe-area-inset-bottom, 0px))`,
    backgroundColor: tabStripBackground,
    transition: "background-color 0.3s ease",
    flexShrink: 0,
    [MOBILE_LANDSCAPE_MQ]: {
      pt: 0.25,
      paddingBottom: `calc(${theme.spacing(1)} + env(safe-area-inset-bottom, 0px))`,
      "& button, & [role='button']": {
        py: 0.35,
        gap: 0.2,
      },
      "& .MuiSvgIcon-root": {
        fontSize: 22,
      },
      "& .MuiTypography-root": {
        fontSize: "10px",
      },
    },
  });

export const getNavTabSx = (
  color: string,
  tabDisabled: boolean,
): SxProps<Theme> => ({
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.35,
  py: 0.5,
  border: "none",
  background: "none",
  cursor: tabDisabled ? "not-allowed" : "pointer",
  opacity: tabDisabled ? 0.45 : 1,
  color,
  transition: "color 0.25s ease",
});

export const getNavTabIconWrapSx = (): SxProps<Theme> => ({
  position: "relative",
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const getNavTabIconSx = (): SxProps<Theme> => ({
  fontSize: 26,
});

export const getLessonsBadgeSx = (): SxProps<Theme> => ({
  position: "absolute",
  top: -2,
  right: -6,
  minWidth: 18,
  height: 18,
  px: 0.4,
  border: "none",
  borderRadius: "999px",
  bgcolor: "#EF4444",
  color: "#fff",
  fontSize: "10px",
  fontWeight: 700,
  lineHeight: "18px",
  textAlign: "center",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

export const getLessonsBadgeProgressSx = (): SxProps<Theme> => ({
  color: "#fff",
});

export const getNavTabLabelSx = (selected: boolean): SxProps<Theme> => ({
  fontSize: "11px",
  fontWeight: selected ? 700 : 600,
});
